import { Dropdown, Modal, Col, Row, Space, Typography, message } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Guid } from "guid-typescript";
import { SearchQuery } from "../code/search-query/search-query";
import { getNotificationsExport } from "../repository/data.repository";
import Loader from "../controls/Loader";
import { Notification } from "../repository/data.default";
import NotificationFormDialog from "../components/NotificationFormDialog";
import NotificationsView from "../components/NotificationsView";
import NotificationView from "../components/NotificationView";
import { NotificationForm } from "../components/NotificationFormDialog.default";
import dayjs from "dayjs";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getProblemMessage } from "../utils/http";

function NotificationsPage() {
  const navigate = useNavigate();
  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [notificationInputMode, setNotificationInputMode] = useState<
    "add" | "update" | "delete" | "view"
  >("add");
  const [notificationInputOpen, setNotificationInputOpen] = useState(false);
  const [notificationInputValues, setNotificationInputValues] =
    useState<NotificationForm>({} as any);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<Notification | undefined>(
    undefined,
  );
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const permissions = useSelector(authorizeSelectors.permissions);

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  // A sent notification opens the read-only delivery detail; a draft routes to the edit/delete dialog.
  function onAction(key: string, record: Notification): void {
    if (key === "view") {
      setViewRecord(record);
      setViewOpen(true);
      return;
    }
    onNotificationInputOpen(key as any, record);
  }

  function onViewReceipts(notification: Notification): void {
    navigate("/notifications/receipts", {
      state: {
        notificationUid: notification.uid,
        notificationTitle: notification.title,
      },
    });
  }

  function onNotificationInputOpen(
    mode: "add" | "update" | "delete" | "view",
    values?: Notification,
  ): void {
    setNotificationInputMode(mode);
    setNotificationInputOpen(true);
    setNotificationInputValues(
      values
        ? {
            uid: values.uid,
            rowVersion: values.rowVersion,
            title: values.title,
            message: values.message,
            scheduledAt: dayjs(values.scheduledAt),
            type:
              typeof values.type === "string"
                ? { display: values.type, value: values.type }
                : null,
          }
        : ({} as any),
    );
  }

  function onNotificationInputFinish(): void {
    setNotificationInputOpen(false);
    setTriggerView(Guid.create().toString());
  }

  function onNotificationInputClose(): void {
    setNotificationInputOpen(false);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading notifications export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getNotificationsExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Notifications
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            disabled={!permissions.can("Notifications", "create")}

            className={`${!permissions.can("Notifications", "create") ? "disabled" : ""}`}
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("Notifications", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={() => onNotificationInputOpen("add")}
          >
            Add Notification
          </Dropdown.Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <NotificationsView
          trigger={triggerView}
          onAction={onAction}
          onSearch={onSearch}
        />
        <NotificationView
          open={viewOpen}
          notification={viewRecord}
          onCancel={() => setViewOpen(false)}
          onViewReceipts={onViewReceipts}
        />
      </Col>
      <NotificationFormDialog
        key={notificationInputMode}
        mode={notificationInputMode}
        open={notificationInputOpen}
        initialValues={notificationInputValues}
        onFinish={onNotificationInputFinish}
        onCancel={onNotificationInputClose}
      />
      {contextHolder}
    </Col>
  );
}

export default NotificationsPage;
